// Toggle hidden attribute on elements inside <include-fragment> and
// <poll-include-fragment> elements that have the data-hide-on-error and
// data-show-on-error attributes on them when error events are fired.

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {compose, fromEvent} from '@github-ui/subscription'
import type {IncludeFragmentElement} from '@github/include-fragment-element'
import {observe} from '@github/selector-observer'
import {on} from 'delegated-events'

observe('include-fragment, poll-include-fragment', {
  subscribe: el => {
    const subscription = compose(fromEvent(el, 'error', onError), fromEvent(el, 'loadstart', onLoad))

    // Race condition fix: include-fragment-element dispatches error events via
    // setTimeout (macrotask), but selector-observer also calls subscribe via a
    // macrotask. If the error fires first, our listener misses it. The element
    // sets the `is-error` class synchronously before the event dispatches, so
    // check for it here to catch any missed errors.
    if (el.classList.contains('is-error')) {
      applyErrorState(el, true)
    }

    return subscription
  },
})

on('click', 'include-fragment button[data-retry-button]', ({currentTarget}) => {
  const includeFragment = currentTarget.closest<IncludeFragmentElement>('include-fragment')!

  // The refetch API was added in v6.1.1 of @github/include-fragment-element:
  // https://github.com/github/include-fragment-element/releases/tag/v6.1.1
  includeFragment.refetch()
})

function toggleElements(event: Event, showError: boolean) {
  const fragment = event.currentTarget
  if (!(fragment instanceof Element)) return

  applyErrorState(fragment, showError, event)
}

function applyErrorState(fragment: Element, showError: boolean, event?: Event) {
  if (isFeatureEnabled('include_fragment_error_handling_fix')) {
    // Ensure the include-fragment fills its container when in error state
    // so the error blankslate has room to render horizontally
    if (fragment instanceof HTMLElement) {
      fragment.style.width = showError ? '100%' : ''
    }

    const hasGenericErrorElements = fragment.querySelectorAll('[data-show-on-error]').length > 0

    // When a fragment has [data-show-on-error] elements, prefer those for any error
    // (they're sized for their container). Only show the [data-show-on-forbidden-error]
    // blankslate as a fallback when no [data-show-on-error] exists.
    const showForbiddenErrorDiv = showError && !hasGenericErrorElements

    for (const el of fragment.querySelectorAll('[data-show-on-forbidden-error]')) {
      if (el instanceof HTMLElement) el.hidden = !showForbiddenErrorDiv
    }
    for (const el of fragment.querySelectorAll('[data-show-on-error]')) {
      if (el instanceof HTMLElement) el.hidden = !showError
    }
  } else {
    // Original behavior: only show forbidden error on 403 status
    const showForbiddenError =
      showError &&
      event instanceof CustomEvent &&
      event.detail?.error?.message?.includes('responded with a status of 403')

    for (const el of fragment.querySelectorAll('[data-show-on-forbidden-error]')) {
      if (el instanceof HTMLElement) el.hidden = !showForbiddenError
    }
    for (const el of fragment.querySelectorAll('[data-show-on-error]')) {
      if (el instanceof HTMLElement) el.hidden = showForbiddenError || !showError
    }
  }
  for (const el of fragment.querySelectorAll('[data-hide-on-error]')) {
    if (el instanceof HTMLElement) el.hidden = showError
  }
}

function onLoad(event: Event) {
  toggleElements(event, false)
}

function onError(event: Event) {
  toggleElements(event, true)
}

export function loadDeferredContentByEvent({currentTarget}: Event) {
  if (!(currentTarget instanceof Element)) return

  loadDeferredContent(currentTarget)
}

export function loadDeferredContent(element: Element) {
  const details = element.closest<HTMLElement>('details')
  if (details) {
    setIncludeFragmentSrc(details)
  }
}

function setIncludeFragmentSrc(detailsElement: HTMLElement) {
  const url = detailsElement.getAttribute('data-deferred-details-content-url')
  if (url) {
    detailsElement.removeAttribute('data-deferred-details-content-url')
    const includeFragmentElement = detailsElement.querySelector<IncludeFragmentElement>(
      'include-fragment, poll-include-fragment',
    )
    if (includeFragmentElement) {
      includeFragmentElement.src = url
    }
  }
}
